'use client';

/**
 * DashboardBootstrap - Deterministic boot gate for dashboard routes
 *
 * This component consolidates the boot sequence for dashboard routes into
 * a single, deterministic state machine. It:
 *
 * 1. Subscribes to existing contexts (SecureAuthContext, CsrfContext)
 * 2. Tracks profile/org loading via RTK Query
 * 3. Dispatches boot status to Redux for Sentry tagging
 * 4. Gates children until fully ready
 * 5. Handles redirects (unauthenticated) and errors (blocked) deterministically
 *
 * For dashboard routes, "ready" means:
 * - CSRF token ready
 * - Auth resolved AND authenticated
 * - User profile loaded
 * - Organization context loaded
 *
 * "blocked" means unrecoverable failure (CSRF/profile/org fetch failed
 * repeatedly), NOT just "user needs to login".
 */

import { ReactNode, useEffect, useRef, useCallback, useState } from 'react';
import { useSecureAuthContext } from '@/core/auth/services';
import { useCsrfContext } from '@/core/providers/CsrfProvider';
import { useGetUserProfileQuery } from '@/api/features/users';
import { useGetOrganizationDetailsQuery } from '@/api/features/organizations';
import { useAppDispatch, useAppSelector } from '@/core/store/hooks';
import {
  bootStart,
  bootSubStateUpdate,
  bootReady,
  bootBlocked,
  bootReset,
  selectBootStatus,
  selectBootSubStates,
  selectBootReason,
} from '@/core/store/slices/bootSlice';
import { getBootId, getTimeSinceBoot } from '@/core/utils/bootSession';
import { authLogger } from '@/core/utils/logger';
import LoadingSystem from '@/shared/components/loading/LoadingSystem';
import { setTag, captureMessage, setContext, setUser } from '@sentry/react';
import { suppressAsync } from '@/shared/utils/asyncHelpers';
import { markSessionInvalidated } from '@/api/core/baseQueryWithRetry';
import { setSessionStatus } from '@/core/auth/sessionStatus';
import { BRAND_BLUE } from '@voley/shared-types/constants';
import { tokenService } from '@/core/auth/services';

interface DashboardBootstrapProps {
  children: ReactNode;
  /** Optional custom loading component */
  loadingFallback?: ReactNode;
  /** Optional custom error component */
  errorFallback?: (error: string, onRetry: () => void) => ReactNode;
}

/** Max time to wait for boot before showing timeout error */
const BOOT_TIMEOUT_MS = 30000;

/** Max retries for critical data before blocking */
const MAX_CRITICAL_RETRIES = 3;

/** Base delay for exponential backoff on transient error retries (ms) */
const RETRY_BASE_DELAY_MS = 2000;

export default function DashboardBootstrap({
  children,
  loadingFallback,
  errorFallback,
}: DashboardBootstrapProps) {
  const dispatch = useAppDispatch();
  const bootStatus = useAppSelector(selectBootStatus);
  const subStates = useAppSelector(selectBootSubStates);
  const bootReason = useAppSelector(selectBootReason);

  // E2E BYPASS: When running E2E tests, skip the entire boot sequence
  // and render children immediately. This avoids race conditions with
  // mock auth setup and allows tests to proceed without waiting for
  // real backend calls.
  // Check synchronously on every render to avoid race conditions.
  const hasE2EMockCookie = typeof document !== 'undefined' && document.cookie.includes('e2e_mock_auth=1');
  const hasPerfBootFlag = typeof document !== 'undefined' && document.cookie.includes('e2e_perf_boot=1');
  // E2E fast-path: skip boot gate entirely UNLESS perf boot tests are running
  const isE2EMode = hasE2EMockCookie && !hasPerfBootFlag;

  // Dispatch bootReady in E2E mode to update Redux state for health panel
  const e2eBootDispatched = useRef(false);
  useEffect(() => {
    if (isE2EMode && !e2eBootDispatched.current) {
      e2eBootDispatched.current = true;
      // Set all sub-states to ready
      dispatch(bootSubStateUpdate({
        csrfReady: true,
        authResolved: true,
        profileLoaded: true,
        orgLoaded: true,
      }));
      dispatch(bootReady());
      authLogger.info('[DashboardBootstrap] E2E mode - dispatched bootReady');
    }
  }, [isE2EMode, dispatch]);

  // Refs to prevent duplicate work
  const bootStarted = useRef(false);
  const redirectTriggered = useRef(false);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);
  const retryTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const retryCount = useRef(0);

  // Track hydration to prevent SSR/client mismatch
  const [isHydrated, setIsHydrated] = useState(false);
  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect -- one-time hydration flag on mount
    setIsHydrated(true);
  }, []);

  // Subscribe to existing providers
  const { isAuthenticated, isLoading: authLoading, isLoggingOut, user } = useSecureAuthContext();
  const { csrfToken, isLoading: csrfLoading, error: csrfError } = useCsrfContext();

  // Fetch critical data via RTK Query (skip until auth AND csrf are resolved)
  const authResolved = !authLoading;
  const csrfResolved = !csrfLoading; // CSRF check complete (success or error)
  const shouldFetchProfile = authResolved && csrfResolved && isAuthenticated;

  const {
    data: userProfile,
    isSuccess: profileSuccess,
    isError: profileError,
    error: profileErrorData,
    isFetching: profileFetching,
    refetch: refetchProfile,
  } = useGetUserProfileQuery(undefined, {
    skip: !shouldFetchProfile,
    refetchOnFocus: false,
    refetchOnReconnect: false,
  });

  // Profile query instrumentation - deduped to fire only on state transitions
  if (process.env.NODE_ENV === 'development' && typeof window !== 'undefined') {
    const profileStateKey = `${shouldFetchProfile}-${profileFetching}-${profileSuccess}-${profileError}-${(profileErrorData as { status?: unknown })?.status}`;
    if (window.__lastProfileQueryState !== profileStateKey) {
      // eslint-disable-next-line react-hooks/immutability -- dev-only dedup logging on window global
      window.__lastProfileQueryState = profileStateKey;
      authLogger.debug(
        '%c[PROFILE_QUERY]',
        `background: ${profileError ? '#c00' : profileSuccess ? '#0a0' : '#888'}; color: #fff; padding: 2px 6px;`,
        {
          skip: !shouldFetchProfile,
          fetching: profileFetching,
          success: profileSuccess,
          error: profileError,
          errorStatus: (profileErrorData as { status?: unknown })?.status ?? null,
          // eslint-disable-next-line react-hooks/refs -- dev-only logging, ref read is intentional
          retryCount: retryCount.current,
          timeSinceBoot: getTimeSinceBoot(),
          bootId: getBootId(),
        }
      );
    }
  }

  // Fetch org details once we have the org ID AND profile is fully loaded
  // CRITICAL: Do NOT fire org request until profile fetch is complete and stable.
  // This prevents race conditions where organizationId is temporarily undefined
  // during auth state transitions after login redirect.
  const organizationId = userProfile?.organizationId;
  const shouldFetchOrg = Boolean(
    organizationId &&
    profileSuccess &&
    !profileFetching &&
    userProfile
  );

  // Dev-only: Log org fetch gating for debugging first-load issues
  if (process.env.NODE_ENV === 'development' && typeof window !== 'undefined') {
    const orgGateKey = `${organizationId}-${profileSuccess}-${profileFetching}`;
    if (window.__lastOrgGateState !== orgGateKey) {
      // eslint-disable-next-line react-hooks/immutability -- dev-only dedup logging on window global
      window.__lastOrgGateState = orgGateKey;
      authLogger.debug('[ORG_FETCH_GATE]', {
        organizationId: organizationId ?? 'UNDEFINED',
        profileSuccess,
        profileFetching,
        shouldFetchOrg,
        timeSinceBoot: getTimeSinceBoot(),
      });
    }
  }

  const {
    data: orgDetails,
    isSuccess: orgSuccess,
    isError: orgError,
    error: orgErrorData,
    isFetching: orgFetching,
    refetch: refetchOrg,
  } = useGetOrganizationDetailsQuery(organizationId ?? '', {
    skip: !shouldFetchOrg,
  });

  /**
   * Check if an error is transient (should be retried).
   * Only retry on 5xx, timeout, or network errors - NOT on 401/403/404.
   */
  const isTransientError = useCallback((error: unknown): boolean => {
    if (!error) return false;
    const err = error as { status?: number | string; originalStatus?: number; name?: string };
    const status = err.status ?? err.originalStatus;

    // 5xx server errors are transient
    if (typeof status === 'number' && status >= 500) return true;

    // Network/fetch errors are transient (RTK Query uses 'FETCH_ERROR' string)
    if (status === 'FETCH_ERROR' || err.name === 'AbortError') return true;

    // 401/403/404 are NOT transient - don't retry
    return false;
  }, []);

  // Derive sub-state readiness
  const csrfReady = !csrfLoading && (!!csrfToken || !!csrfError);
  const profileLoaded = profileSuccess && !!userProfile && !profileFetching;
  const orgLoaded = orgSuccess && !!orgDetails && !orgFetching;

  // ALWAYS log boot gating state for debugging
  const gatingState = {
    bootStatus,
    authLoading,
    authResolved,
    csrfLoading,
    csrfResolved,
    isAuthenticated,
    hasUser: !!user,
    shouldFetchProfile,
    csrfReady,
    csrfToken: csrfToken ? 'present' : 'missing',
    csrfError: csrfError ? String(csrfError) : null,
    profileLoaded,
    profileFetching,
    profileError: !!profileError,
    profileErrorStatus: (profileErrorData as { status?: number | string })?.status,
    orgLoaded,
    orgFetching,
    orgError: !!orgError,
    orgErrorStatus: (orgErrorData as { status?: number | string })?.status,
    timeSinceBoot: getTimeSinceBoot(),
  };
  // Only log when state changes to avoid spam
  if (typeof window !== 'undefined') {
    const stateKey = JSON.stringify(gatingState);
    if (window.__lastBootGatingState !== stateKey) {
      // eslint-disable-next-line react-hooks/immutability -- dev-only dedup logging on window global
      window.__lastBootGatingState = stateKey;
      if (process.env.NODE_ENV === 'development') {
        authLogger.debug('[BOOT_DEBUG] Gating state changed', gatingState);
      }
      authLogger.info('[DashboardBootstrap] Gating state:', gatingState);
    }
  }

  // Start boot sequence on mount
  useEffect(() => {
    if (!bootStarted.current && isHydrated) {
      bootStarted.current = true;
      dispatch(bootStart({ routeContext: 'dashboard' }));
      authLogger.info('[DashboardBootstrap] Boot started', { bootId: getBootId() });

      // Tag Sentry with boot context immediately
      setTag('boot_status', 'booting');
      setTag('boot_id', getBootId());
      setTag('boot_route', 'dashboard');

      // Set up timeout
      timeoutRef.current = setTimeout(() => {
        authLogger.error('[DashboardBootstrap] Boot timeout', {
          bootId: getBootId(),
          timeSinceBoot: getTimeSinceBoot(),
          subStates,
        });
        dispatch(bootBlocked({ reason: 'Boot timeout - please refresh the page' }));

        setTag('boot_status', 'blocked');
        captureMessage('Dashboard boot timeout', {
          level: 'error',
          tags: {
            boot_id: getBootId(),
            csrf_ready: String(csrfReady),
            auth_resolved: String(authResolved),
            profile_loaded: String(profileLoaded),
            org_loaded: String(orgLoaded),
            time_since_boot_ms: String(getTimeSinceBoot()),
          },
        });
      }, BOOT_TIMEOUT_MS);
    }

    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
      if (retryTimeoutRef.current) {
        clearTimeout(retryTimeoutRef.current);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, isHydrated]); // Only on mount

  // Update sub-states in Redux as they change
  useEffect(() => {
    dispatch(bootSubStateUpdate({
      csrfReady,
      authResolved,
      profileLoaded,
      orgLoaded,
    }));
  }, [dispatch, csrfReady, authResolved, profileLoaded, orgLoaded]);

  // Handle CSRF failure (unrecoverable)
  useEffect(() => {
    if (csrfError && !csrfToken) {
      authLogger.error('[DashboardBootstrap] CSRF initialization failed', { error: csrfError });
      dispatch(bootBlocked({ reason: 'Security initialization failed. Please refresh the page.' }));

      setTag('boot_status', 'blocked');
      captureMessage('Dashboard CSRF initialization failed', {
        level: 'error',
        tags: {
          boot_id: getBootId(),
          time_since_boot_ms: String(getTimeSinceBoot()),
        },
        extra: {
          csrfError: typeof csrfError === 'string' ? csrfError : JSON.stringify(csrfError),
        },
      });
    }
  }, [csrfError, csrfToken, dispatch]);

  // Handle profile/org fetch failures
  useEffect(() => {
    if (!profileError && !orgError) return;

    const errorData = profileError ? profileErrorData : orgErrorData;
    const errorStatus = (errorData as { status?: number })?.status;

    authLogger.warn('[DashboardBootstrap] Critical data fetch failed', {
      profileError,
      orgError,
      errorStatus,
      retryCount: retryCount.current,
    });

    // 401 means auth is invalid - redirect to login (don't block)
    if (errorStatus === 401) {
      const error401Context = {
        profileError: !!profileError,
        orgError: !!orgError,
        errorStatus,
        timeSinceBoot: getTimeSinceBoot(),
        bootStatus,
        authResolved,
        isAuthenticated,
      };
      if (process.env.NODE_ENV === 'development') {
        authLogger.error('[BOOT_DEBUG] 401 ERROR from profile/org fetch!', error401Context);
      }
      authLogger.warn('[DashboardBootstrap] 401 ERROR from profile/org fetch', error401Context);

      if (!redirectTriggered.current) {
        redirectTriggered.current = true;
        const currentPath = window.location.pathname;
        const currentSearch = window.location.search;
        const returnUrl = encodeURIComponent(currentPath + currentSearch);
        window.location.href = `/login?returnUrl=${returnUrl}`;
      }
      return;
    }

    // 403 means forbidden - block immediately (no retry)
    if (errorStatus === 403) {
      const reason = 'Access denied. Please contact your administrator.';
      dispatch(bootBlocked({ reason }));
      captureMessage('Dashboard access forbidden', {
        level: 'error',
        tags: { boot_id: getBootId(), error_status: '403' },
      });
      return;
    }

    // Only count transient errors (5xx, network) for retry logic
    if (isTransientError(errorData)) {
      // Clear any pending retry before scheduling a new one
      if (retryTimeoutRef.current) {
        clearTimeout(retryTimeoutRef.current);
        retryTimeoutRef.current = null;
      }

      retryCount.current += 1;

      if (retryCount.current >= MAX_CRITICAL_RETRIES) {
        const reason = profileError
          ? 'Unable to load your profile. Please try again.'
          : 'Unable to load organization. Please try again.';
        dispatch(bootBlocked({ reason }));

        setTag('boot_status', 'blocked');
        captureMessage('Dashboard critical data fetch failed', {
          level: 'error',
          tags: {
            boot_id: getBootId(),
            profile_error: String(!!profileError),
            org_error: String(!!orgError),
            error_status: String(errorStatus),
            retry_count: String(retryCount.current),
            time_since_boot_ms: String(getTimeSinceBoot()),
          },
        });
      } else {
        // Auto-retry with exponential backoff for transient errors
        // Delay: 2s, 4s, 8s (capped at 10s)
        const delay = Math.min(RETRY_BASE_DELAY_MS * Math.pow(2, retryCount.current - 1), 10000);
        authLogger.info('[DashboardBootstrap] Transient error, scheduling retry', {
          retryCount: retryCount.current,
          maxRetries: MAX_CRITICAL_RETRIES,
          delayMs: delay,
          errorStatus,
        });

        retryTimeoutRef.current = setTimeout(() => {
          if (profileError) {
            suppressAsync(
              refetchProfile(),
              { operation: 'autoRetryProfile', data: { bootId: getBootId(), attempt: retryCount.current } }
            );
          } else if (orgError && organizationId) {
            suppressAsync(
              refetchOrg(),
              { operation: 'autoRetryOrg', data: { bootId: getBootId(), attempt: retryCount.current } }
            );
          }
        }, delay);
      }
    } else {
      // Non-transient error (4xx other than 401/403) - block immediately
      const reason = profileError
        ? 'Unable to load your profile.'
        : 'Unable to load organization.';
      dispatch(bootBlocked({ reason }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [profileError, orgError, profileErrorData, orgErrorData, dispatch, isTransientError, refetchProfile, refetchOrg, organizationId]);

  // Listen for session_invalid events from SocketClient (e.g., max token refresh failures)
  useEffect(() => {
    const handleSessionInvalid = (event: CustomEvent<{ reason: string; failures?: number; timestamp?: number }>) => {
      authLogger.warn('[DashboardBootstrap] Received auth:session_invalid event', event.detail);

      if (redirectTriggered.current) {
        authLogger.info('[DashboardBootstrap] Redirect already triggered, ignoring session_invalid');
        return;
      }

      // Mark REST layer as invalidated BEFORE showing overlay to prevent request pile-up
      markSessionInvalidated();
      redirectTriggered.current = true;

      authLogger.info('[DashboardBootstrap] Session invalid, showing overlay', {
        reason: event.detail?.reason,
      });

      setSessionStatus('expired');
    };

    window.addEventListener('auth:session_invalid', handleSessionInvalid as EventListener);

    return () => {
      window.removeEventListener('auth:session_invalid', handleSessionInvalid as EventListener);
    };
  }, []);

  // Listen for session_replaced events (user signed in from another location)
  useEffect(() => {
    const handleSessionReplaced = () => {
      authLogger.warn('[DashboardBootstrap] Received auth:session_replaced event');

      if (redirectTriggered.current) {
        authLogger.info('[DashboardBootstrap] Redirect already triggered, ignoring session_replaced');
        return;
      }

      markSessionInvalidated();
      redirectTriggered.current = true;

      authLogger.info('[DashboardBootstrap] Session replaced, showing overlay');

      setSessionStatus('replaced');
    };

    window.addEventListener('auth:session_replaced', handleSessionReplaced as EventListener);

    return () => {
      window.removeEventListener('auth:session_replaced', handleSessionReplaced as EventListener);
    };
  }, []);

  // Handle unauthenticated redirect (not a "block" - this is expected flow)
  useEffect(() => {
    // CRITICAL FIX: Don't check auth or trigger redirects until hydration is complete
    // This prevents redirects based on pre-hydration state (which may be stale/incorrect)
    if (!isHydrated) {
      return;
    }

    // E2E BYPASS: Skip redirect when running E2E tests with mock auth.
    // The e2e_mock_auth cookie indicates we're in test mode and should trust
    // the auth setup even if React state hasn't fully synchronized yet.
    const cookieStr = typeof document !== 'undefined' ? document.cookie : '';
    const hasE2EMockAuth = cookieStr.includes('e2e_mock_auth=1');

    if (process.env.NODE_ENV === 'development') {
      authLogger.debug('[DashboardBootstrap] Redirect check - cookies:', cookieStr);
      authLogger.debug('[DashboardBootstrap] Redirect check - hasE2EMockAuth:', hasE2EMockAuth);
      authLogger.debug('[DashboardBootstrap] Redirect check - authResolved:', authResolved, 'isAuthenticated:', isAuthenticated);
    }

    if (hasE2EMockAuth) {
      authLogger.info('[DashboardBootstrap] E2E mock auth detected, skipping redirect check');
      return;
    }

    if (authResolved && !isAuthenticated && !redirectTriggered.current) {
      // Before redirecting, check if a token refresh is in progress or can be attempted.
      // The token may be temporarily expired during a normal refresh cycle — we should
      // wait for the refresh to complete rather than prematurely redirecting to /login.
      const refreshPromise = tokenService.getRefreshPromise();
      if (refreshPromise) {
        authLogger.info('[DashboardBootstrap] Auth expired but refresh in progress, waiting...');
        let cancelled = false;
        refreshPromise.then(() => {
          if (!cancelled) {
            authLogger.info('[DashboardBootstrap] Refresh completed during redirect check, auth should restore');
          }
        }).catch(() => {
          // Refresh failed — the useEffect will re-run with updated isAuthenticated
          if (!cancelled) {
            authLogger.warn('[DashboardBootstrap] Refresh failed during redirect check');
          }
        });
        return () => { cancelled = true; };
      }

      // No refresh in progress — attempt one before giving up.
      // This handles the case where the token expired while the tab was backgrounded
      // and the scheduled refresh timer was throttled by the browser.
      const attemptRefreshBeforeRedirect = async () => {
        try {
          const { secureAuthService } = await import('@/core/auth/services');
          authLogger.info('[DashboardBootstrap] Attempting token refresh before redirect...');
          await secureAuthService.refreshAccessToken();
          authLogger.info('[DashboardBootstrap] Refresh succeeded, cancelling redirect');
          // React will re-render with isAuthenticated=true — no redirect needed
        } catch {
          // Refresh definitively failed — now redirect
          if (redirectTriggered.current) return; // Already handled
          redirectTriggered.current = true;

          authLogger.warn('[DashboardBootstrap] Refresh failed, redirecting to login');

          setTimeout(() => {
            if (typeof window !== 'undefined') {
              const currentPath = window.location.pathname;
              const currentSearch = window.location.search;
              const returnUrl = encodeURIComponent(currentPath + currentSearch);
              window.location.href = `/login?returnUrl=${returnUrl}`;
            }
          }, 500);
        }
      };

      // ALWAYS log redirect trigger with full context
      const redirectContext = {
        authResolved,
        isAuthenticated,
        authLoading,
        user: !!user,
        timeSinceBoot: getTimeSinceBoot(),
        csrfReady,
        csrfToken: csrfToken ? 'present' : 'missing',
        profileLoaded,
        profileError: !!profileError,
        profileErrorStatus: (profileErrorData as { status?: number | string })?.status,
        orgLoaded,
      };
      if (process.env.NODE_ENV === 'development') {
        authLogger.error('[BOOT_DEBUG] REDIRECT CHECK - Not authenticated, attempting refresh first', redirectContext);
      }
      authLogger.warn('[DashboardBootstrap] REDIRECT CHECK - Not authenticated, attempting refresh', redirectContext);

      attemptRefreshBeforeRedirect();
    }

    // Reset redirect flag if auth is restored
    if (isAuthenticated) {
      redirectTriggered.current = false;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authResolved, isAuthenticated, isHydrated]);

  // Transition to ready when all conditions met
  useEffect(() => {
    if (
      bootStatus === 'booting' &&
      csrfReady &&
      authResolved &&
      isAuthenticated &&
      profileLoaded &&
      orgLoaded
    ) {
      // Clear timeout
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }

      const bootDuration = getTimeSinceBoot();
      dispatch(bootReady());
      authLogger.info('[DashboardBootstrap] Boot complete', {
        bootId: getBootId(),
        timeSinceBoot: bootDuration,
      });

      // Tag Sentry with boot context
      setTag('boot_status', 'ready');
      setTag('boot_id', getBootId());
      setContext('boot', {
        boot_id: getBootId(),
        boot_duration_ms: bootDuration,
        route_context: 'dashboard',
      });

      // Identify user in Sentry (opaque IDs only — no PII)
      if (userProfile?.id) {
        setUser({ id: String(userProfile.id) });
      }
      if (orgDetails?.id) {
        setTag('organization_id', String(orgDetails.id));
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bootStatus, csrfReady, authResolved, isAuthenticated, profileLoaded, orgLoaded, dispatch]);

  // Retry handler for blocked state
  const handleRetry = useCallback(() => {
    // If CSRF failed, a page refresh is required (CsrfProvider doesn't support refetch)
    if (csrfError && !csrfToken) {
      authLogger.info('[DashboardBootstrap] Retry requires page refresh for CSRF');
      window.location.reload();
      return;
    }

    // Reset local state
    retryCount.current = 0;
    bootStarted.current = false;
    redirectTriggered.current = false;

    // Reset boot slice to cold (allows bootStart to work)
    dispatch(bootReset());

    // Force refetch critical data via RTK Query
    // refetch() triggers new requests regardless of cache/error state
    // Use suppressAsync to properly handle rejections and log to Sentry
    if (authResolved && isAuthenticated) {
      suppressAsync(
        refetchProfile(),
        { operation: 'retryRefetchProfile', data: { bootId: getBootId() } }
      );
      // Also refetch org if we have an org ID (profile succeeded but org failed)
      if (organizationId) {
        suppressAsync(
          refetchOrg(),
          { operation: 'retryRefetchOrg', data: { bootId: getBootId(), organizationId } }
        );
      }
    }

    // Small delay to let reset propagate, then restart boot
    retryTimeoutRef.current = setTimeout(() => {
      dispatch(bootStart({ routeContext: 'dashboard' }));
    }, 50);

    authLogger.info('[DashboardBootstrap] Retry initiated');
  }, [dispatch, csrfError, csrfToken, authResolved, isAuthenticated, organizationId, refetchProfile, refetchOrg]);

  // Render based on boot status

  // In E2E mode, skip boot gating and render children directly with ready marker.
  if (isE2EMode) {
    return (
      <div data-testid="dashboard-app-ready" data-boot-status="ready">
        {children}
      </div>
    );
  }

  // LOGOUT OVERLAY: Show immediately when logging out to prevent visual flash
  // This is checked BEFORE boot status to ensure smooth transition
  if (isLoggingOut) {
    return (
      <LoadingSystem
        show={true}
        message="Signing out..."
        size="large"
        fullScreen
      />
    );
  }

  if (bootStatus === 'blocked') {
    if (errorFallback) {
      // eslint-disable-next-line react-hooks/refs -- handleRetry is a callback; refs are read on click, not during render
      return <>{errorFallback(bootReason || 'An error occurred', handleRetry)}</>;
    }

    const brandBlue = BRAND_BLUE;

    return (
      <div
        className="min-h-screen flex items-center justify-center p-4"
        style={{ backgroundColor: '#F8FAFB' }}
      >
        {/* Subtle gradient background */}
        <div
          className="fixed inset-0 pointer-events-none"
          style={{
            background:
              'radial-gradient(ellipse at top right, rgba(53, 129, 207, 0.04) 0%, transparent 50%), radial-gradient(ellipse at bottom left, rgba(53, 129, 207, 0.03) 0%, transparent 50%)',
          }}
        />

        <div className="max-w-md w-full text-center relative z-10">
          {/* Error Icon */}
          <div
            className="mx-auto w-16 h-16 rounded-full flex items-center justify-center mb-6"
            style={{ backgroundColor: '#FEE2E2' }}
          >
            <svg
              className="w-8 h-8"
              style={{ color: '#DC2626' }}
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
              />
            </svg>
          </div>

          {/* Title */}
          <h2 className="text-xl font-semibold mb-2" style={{ color: '#111827' }}>
            Unable to load workspace
          </h2>

          {/* Description */}
          <p className="mb-6" style={{ color: '#6B7280' }}>
            {bootReason || 'An unexpected error occurred while loading your workspace.'}
          </p>

          {/* Action Button */}
          <button
            onClick={handleRetry}
            className="px-6 py-2.5 rounded-full font-medium text-white inline-flex items-center justify-center gap-2 transition-all duration-200"
            style={{
              backgroundColor: brandBlue,
              boxShadow: '0 1px 2px 0 rgb(0 0 0 / 0.05)',
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = 'scale(1.02)';
              e.currentTarget.style.boxShadow = '0 4px 6px -1px rgb(0 0 0 / 0.1)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = 'scale(1)';
              e.currentTarget.style.boxShadow = '0 1px 2px 0 rgb(0 0 0 / 0.05)';
            }}
          >
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
              />
            </svg>
            Try Again
          </button>

          {/* Help Text */}
          <p className="mt-6 text-sm" style={{ color: '#9CA3AF' }}>
            If this problem persists, please contact{' '}
            <a
              href="mailto:support@voley.us"
              className="underline hover:no-underline"
              style={{ color: brandBlue }}
            >
              support@voley.us
            </a>
          </p>
        </div>
      </div>
    );
  }

  // Not yet ready - show loading
  if (bootStatus !== 'ready') {
    // CRITICAL FIX: Use consistent message until hydration completes
    // This prevents SSR/client text mismatch that causes hydration errors
    // The message must be identical on server and first client render
    if (!isHydrated) {
      return (
        <LoadingSystem
          show={true}
          message="Preparing your workspace..."
          size="large"
          fullScreen
        />
      );
    }

    // After hydration, we can safely show dynamic messages based on state
    // If redirecting to login, show redirect message
    if (authResolved && !isAuthenticated) {
      return (
        <LoadingSystem
          show={true}
          message="Redirecting to login..."
          size="large"
          fullScreen
        />
      );
    }

    if (loadingFallback) {
      return <>{loadingFallback}</>;
    }

    // Determine loading message based on current phase
    let message = 'Preparing your workspace...';
    if (!csrfReady) {
      message = 'Initializing security...';
    } else if (!authResolved) {
      message = 'Checking authentication...';
    } else if (!profileLoaded) {
      // eslint-disable-next-line react-hooks/refs -- reading retry count for loading message display
      message = retryCount.current > 0 && profileError
        // eslint-disable-next-line react-hooks/refs
        ? `Retrying... (attempt ${retryCount.current + 1}/${MAX_CRITICAL_RETRIES + 1})`
        : 'Loading your profile...';
    } else if (!orgLoaded) {
      // eslint-disable-next-line react-hooks/refs -- reading retry count for loading message display
      message = retryCount.current > 0 && orgError
        // eslint-disable-next-line react-hooks/refs
        ? `Retrying... (attempt ${retryCount.current + 1}/${MAX_CRITICAL_RETRIES + 1})`
        : 'Loading organization...';
    }

    return (
      <LoadingSystem
        show={true}
        message={message}
        size="large"
        fullScreen
      />
    );
  }

  // Boot complete - render children with app-ready marker for E2E tests
  return (
    <div data-testid="dashboard-app-ready" data-boot-status={bootStatus}>
      {children}
    </div>
  );
}
